import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { webApi } from "@/lib/webApi";
import { useAuth } from "@/context/AuthContext";

const FollowsContext = createContext(null);

export const FollowsProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState([]);

  useEffect(() => {
    if (!user) return setIds([]);
    webApi.get("/follows").then((r) => setIds(r.data.map((f) => f.seller_id))).catch(() => {});
  }, [user]);

  const toggle = useCallback(async (sellerId, name) => {
    const following = ids.includes(sellerId);
    setIds((l) => (following ? l.filter((x) => x !== sellerId) : [sellerId, ...l]));
    try {
      if (following) await webApi.delete(`/follows/${sellerId}`);
      else await webApi.post(`/follows/${sellerId}`);
      toast(following ? `Unfollowed ${name || "shop"}` : `Following ${name || "shop"} — their new pieces will show first`);
    } catch (e) {
      setIds((l) => (following ? [sellerId, ...l] : l.filter((x) => x !== sellerId)));
      toast.error(e?.response?.data?.detail || "Couldn't update. Please try again.");
    }
  }, [ids]);

  return <FollowsContext.Provider value={{ ids, toggle }}>{children}</FollowsContext.Provider>;
};

export const useFollows = () => useContext(FollowsContext);

export const FollowButton = ({ sellerId, name, className = "", tone = "dark" }) => {
  const { user } = useAuth();
  const { ids, toggle } = useFollows();
  const navigate = useNavigate();
  if (user?.user_id === sellerId) return null;
  const following = ids.includes(sellerId);
  return (
    <button
      data-testid="follow-shop-button"
      aria-pressed={following}
      onClick={() => (user ? toggle(sellerId, name) : navigate("/login"))}
      className={`px-6 py-2.5 min-h-[44px] text-[10px] tracking-[0.3em] uppercase border transition-colors ${
        tone === "light"
          ? following ? "bg-[#0F0F0F] border-[#0F0F0F] text-[#C5A059]" : "border-[#0F0F0F] text-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-[#C5A059]"
          : following ? "bg-[#C5A059] border-[#C5A059] text-[#0F0F0F]" : "border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-[#0F0F0F]"
      } ${className}`}
    >
      {following ? "Following" : "Follow shop"}
    </button>
  );
};
